import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition
 *
 * Wraps route content and plays a lightweight CSS enter/exit animation
 * on every location change (pathname only — hash/search changes are ignored).
 *
 * Enter : opacity 0 → 1  +  translateY 8px → 0  over 250ms ease-out
 * Exit  : opacity 1 → 0                          over 150ms ease-in
 *
 * Implemented entirely with CSS transitions — no Framer Motion, no GSAP.
 * Respects `prefers-reduced-motion` by collapsing durations to 1ms.
 */
const PageTransition = ({ children }) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [phase, setPhase] = useState('entered'); // 'entered' | 'exiting' | 'entering'
  const prevPathnameRef = useRef(location.pathname);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Only animate on pathname changes — ignore query/hash updates within the same page
    if (location.pathname === prevPathnameRef.current) {
      // Same page: just swap children silently (e.g. filter param change)
      setDisplayChildren(children);
      return;
    }

    prevPathnameRef.current = location.pathname;

    // Cancel any in-flight animation
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);

    // Phase 1 — exit (fade out current content)
    setPhase('exiting');

    timerRef.current = setTimeout(() => {
      // Phase 2 — swap content while invisible, then start enter
      setDisplayChildren(children);
      setPhase('entering');

      // Phase 3 — next frame: trigger enter transition
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setPhase('entered');
        });
      });
    }, 150); // matches exit duration

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Keep displayChildren up-to-date when content inside the same page updates
  // (e.g. auth state resolves, data loads) — but only when we're not mid-animation
  useEffect(() => {
    if (phase === 'entered') {
      setDisplayChildren(children);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <div
      className={`page-transition page-transition--${phase}`}
      aria-live="polite"
    >
      {displayChildren}
    </div>
  );
};

export default PageTransition;
